import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { tolerantId } from "../../utils/firestoreHelpers";
import { cargarPersonalEdificio } from "../../utils/personalEdificio";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import {
  FaTasks,
  FaPlus,
  FaUser,
  FaCalendarAlt,
  FaFilter,
  FaEdit,
  FaTrash,
  FaSearch,
} from "react-icons/fa";
import EmptyState from "../../components/EmptyState";
import StatusBadge from "../../components/StatusBadge";
import AdminModal from "../../components/AdminModal";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnGhost, inputBase, pageContainer, pageTitle } from "../../utils/designTokens";

const TareasAdmin = () => {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    employeeid: "",
    title: "",
    description: "",
    dueDate: "",
    priority: "media",
  });
  const [saving, setSaving] = useState(false);

  const buildingId = selectedBuilding?.buildingid;

  useEffect(() => {
    if (!buildingId) return;
    fetchEmployees();
    fetchTasks();
  }, [buildingId]);

  // Personal del edificio: employeebuilding por `building` + nombre de la
  // ficha users (utils/personalEdificio). La colección employee no se lee:
  // con las reglas 0D el staff no la puede listar.
  const fetchEmployees = async () => {
    try {
      setEmployees(await cargarPersonalEdificio(buildingId));
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "employee_tasks"),
        where("buildingid", "in", tolerantId(buildingId))
      );
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Ordenar por assignedAt en el cliente (evita requerir composite index)
      data.sort((a, b) => {
        const da = a.assignedAt?.toDate?.() || a.assignedAt || 0;
        const db2 = b.assignedAt?.toDate?.() || b.assignedAt || 0;
        return db2 - da;
      });
      setTasks(data);
    } catch (err) {
      console.error("Error fetching tasks:", err);
    }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingTask(null);
    setFormData({
      employeeid: "",
      title: "",
      description: "",
      dueDate: "",
      priority: "media",
    });
    setShowModal(true);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      employeeid: String(task.employeeid),
      title: task.title || "",
      description: task.description || "",
      dueDate: task.dueDate
        ? (task.dueDate.toDate
            ? task.dueDate.toDate().toISOString().split("T")[0]
            : new Date(task.dueDate).toISOString().split("T")[0])
        : "",
      priority: task.priority || "media",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.employeeid) {
      showToast("Completá el empleado y el título", "warning");
      return;
    }
    setSaving(true);
    try {
      const emp = employees.find(
        (e) => String(e.employeeid) === String(formData.employeeid)
      );
      const empName = emp
        ? `${emp.nombre || ""} ${emp.apellido || ""}`.trim()
        : "";

      const taskData = {
        employeeid: String(formData.employeeid),
        employeeName: empName,
        buildingid: Number(buildingId),
        title: formData.title.trim(),
        description: formData.description.trim(),
        dueDate: formData.dueDate
          ? Timestamp.fromDate(new Date(formData.dueDate + "T00:00:00"))
          : null,
        priority: formData.priority,
      };

      if (editingTask) {
        await updateDoc(doc(db, "employee_tasks", editingTask.id), {
          ...taskData,
          updatedAt: Timestamp.now(),
        });
      } else {
        await addDoc(collection(db, "employee_tasks"), {
          ...taskData,
          status: "pendiente",
          assignedBy: auth.currentUser?.email || "admin",
          assignedAt: Timestamp.now(),
        });
      }
      setShowModal(false);
      fetchTasks();
    } catch (err) {
      console.error("Error saving task:", err);
      showToast("Error al guardar la tarea", "error");
    }
    setSaving(false);
  };

  const handleDelete = async (taskId) => {
    const ok = await confirmDialog("¿Eliminar esta tarea?", {
      title: "Eliminar tarea",
      danger: true,
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "employee_tasks", taskId));
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(
      (e) => String(e.employeeid) === String(empId)
    );
    return emp
      ? `${emp.nombre || ""} ${emp.apellido || ""}`.trim()
      : "Empleado";
  };

  const formatDate = (val) => {
    if (!val) return "-";
    const d = val?.toDate ? val.toDate() : new Date(val);
    return d.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.status === "completada") return false;
    const d = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    return d < new Date();
  };

  const STATUS_ORDER = { pendiente: 0, en_progreso: 1, completada: 2 };
  const filteredTasks = tasks.filter((t) => {
    if (filterEmployee && String(t.employeeid) !== String(filterEmployee))
      return false;
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    return true;
  }).sort((a, b) => {
    const sa = STATUS_ORDER[a.status] ?? 3;
    const sb = STATUS_ORDER[b.status] ?? 3;
    if (sa !== sb) return sa - sb;
    const da = a.dueDate?.toDate ? a.dueDate.toDate() : a.dueDate ? new Date(a.dueDate) : new Date(9999, 0);
    const db2 = b.dueDate?.toDate ? b.dueDate.toDate() : b.dueDate ? new Date(b.dueDate) : new Date(9999, 0);
    return da - db2;
  });

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState
          icon={FaTasks}
          title="Seleccioná un edificio para gestionar tareas"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Encabezado */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaTasks style={{ marginRight: 10, color: colors.primary }} />
          Tareas de Empleados
        </h1>
        <button style={styles.newBtn} onClick={openCreateModal}>
          <FaPlus style={{ marginRight: 8 }} /> Nueva Tarea
        </button>
      </div>

      {/* Filtros */}
      <div style={styles.filtersCard}>
        <div style={styles.filtersHeader}>
          <FaFilter style={{ marginRight: 8, color: colors.primary }} />
          <span style={{ fontWeight: 400 }}>Filtros</span>
        </div>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Empleado</label>
            <select
              style={styles.select}
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
            >
              <option value="">Todos</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.employeeid}>
                  {emp.nombre} {emp.apellido}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Estado</label>
            <select
              style={styles.select}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="en_progreso">En Progreso</option>
              <option value="completada">Completada</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Prioridad</label>
            <select
              style={styles.select}
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tarjetas de tareas */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={{ color: colors.gray600, marginTop: 12 }}>Cargando tareas...</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <EmptyState
          icon={FaSearch}
          title="No se encontraron tareas"
          description="Creá una nueva tarea con el botón de arriba"
        />
      ) : (
        <div style={styles.cardList}>
          {filteredTasks.map((task) => {
            const overdue = isOverdue(task);

            return (
              <div
                key={task.id}
                style={{
                  ...styles.taskCard,
                  ...(overdue ? { borderLeft: `4px solid ${colors.error}` } : {}),
                }}
              >
                <div style={styles.taskCardTop}>
                  <div>
                    <h3 style={styles.taskTitle}>{task.title}</h3>
                    <div style={styles.taskMeta}>
                      <span style={styles.taskEmployee}>
                        <FaUser style={{ marginRight: 4, fontSize: 11 }} />
                        {task.employeeName || getEmployeeName(task.employeeid)}
                      </span>
                      <span style={styles.taskDate}>
                        <FaCalendarAlt style={{ marginRight: 4, fontSize: 11 }} />
                        Vence: {formatDate(task.dueDate)}
                        {overdue && (
                          <span style={{ color: colors.error, fontWeight: 400, marginLeft: 6 }}>
                            (Vencida)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div style={styles.taskActions}>
                    <button
                      style={styles.iconBtn}
                      onClick={() => openEditModal(task)}
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                    <button
                      style={{ ...styles.iconBtn, color: colors.error }}
                      onClick={() => handleDelete(task.id)}
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {task.description && (
                  <p style={styles.taskDescription}>{task.description}</p>
                )}

                <div style={styles.taskBadges}>
                  <StatusBadge status={task.priority || "media"} variant="dot" />
                  <StatusBadge status={task.status || "pendiente"} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear/editar tarea */}
      <AdminModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingTask ? "Editar Tarea" : "Nueva Tarea"}
        size="medium"
        footer={
          <>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
              Cancelar
            </button>
            <button
              style={styles.saveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? "Guardando..."
                : editingTask
                ? "Guardar"
                : "Crear Tarea"}
            </button>
          </>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Empleado *</label>
          <select
            style={styles.select}
            value={formData.employeeid}
            onChange={(e) =>
              setFormData({ ...formData, employeeid: e.target.value })
            }
          >
            <option value="">Seleccionar empleado</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.employeeid}>
                {emp.nombre} {emp.apellido}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Título *</label>
          <input
            type="text"
            style={styles.input}
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            placeholder="Ej: Limpiar lobby principal"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Descripción</label>
          <textarea
            style={styles.textarea}
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Detalles de la tarea..."
          />
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Fecha límite</label>
            <input
              type="date"
              style={styles.input}
              value={formData.dueDate}
              onChange={(e) =>
                setFormData({ ...formData, dueDate: e.target.value })
              }
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Prioridad</label>
            <select
              style={styles.select}
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
            >
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>
        </div>
      </AdminModal>
    </div>
  );
};

// ─── Estilos con design tokens ───────────────────────────────────────────────
const styles = {
  container: {
    ...pageContainer,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: spacing[5],
    flexWrap: "wrap",
    gap: spacing[3],
  },
  title: {
    ...pageTitle,
    display: "flex",
    alignItems: "center",
  },
  newBtn: {
    ...btnPrimary,
    fontFamily: typography.fontFamily,
  },
  filtersCard: {
    ...cardBase,
    marginBottom: spacing[6],
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: spacing[4],
    fontSize: 15,
    color: colors.black,
  },
  filtersRow: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minWidth: 160,
  },
  filterLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  select: {
    ...inputBase,
  },
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
  },
  taskCard: {
    ...cardBase,
  },
  taskCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  taskTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 8px",
  },
  taskMeta: {
    display: "flex",
    gap: spacing[4],
    flexWrap: "wrap",
  },
  taskEmployee: {
    display: "flex",
    alignItems: "center",
    fontSize: typography.sizes.base,
    color: colors.gray600,
  },
  taskDate: {
    display: "flex",
    alignItems: "center",
    fontSize: typography.sizes.base,
    color: colors.gray600,
  },
  taskDescription: {
    fontSize: typography.sizes.md,
    color: colors.gray700,
    margin: "12px 0",
    lineHeight: 1.5,
  },
  taskBadges: {
    display: "flex",
    gap: spacing[2],
    marginTop: spacing[3],
  },
  taskActions: {
    display: "flex",
    gap: 6,
  },
  iconBtn: {
    ...btnGhost,
    fontSize: 15,
    padding: 6,
    borderRadius: 6,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: spacing[4],
    flex: 1,
  },
  formLabel: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray700,
  },
  input: {
    ...inputBase,
    boxSizing: "border-box",
  },
  textarea: {
    ...inputBase,
    resize: "vertical",
    boxSizing: "border-box",
  },
  formRow: {
    display: "flex",
    gap: spacing[4],
  },
  cancelBtn: {
    ...btnSecondary,
    fontFamily: typography.fontFamily,
  },
  saveBtn: {
    ...btnPrimary,
    fontFamily: typography.fontFamily,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  spinner: {
    width: 36,
    height: 36,
    border: `3px solid ${colors.gray200}`,
    borderTopColor: colors.primary,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

export default TareasAdmin;
